import { useEffect, useRef } from 'react';
import { ConfirmModal } from '../../components/ConfirmModal';
import { TextInput } from '../../components/TextInput';
import type { TrialErrorField, TrialErrorTarget } from './useOrganizationTrial';
import './OrganizationTrial.css';

interface SetTrialModalProps {
  isOpen: boolean;
  organizationName: string;
  /** True when the organization already has a window, which changes the title and the button. */
  isEditing: boolean;
  startDate: string;
  endDate: string;
  onStartDateChange: (value: string) => void;
  onEndDateChange: (value: string) => void;
  loading: boolean;
  error: string | null;
  /** Whether `error` is about what was typed or about the server refusing. */
  errorTarget: TrialErrorTarget | null;
  /** Which date field a typed-input error belongs to. */
  errorField: TrialErrorField | null;
  /**
   * Which rejection this is. Changes on every rejection, including a repeat of the one already on
   * screen, which is the only thing that separates those two events.
   */
  errorSeq: number | null;
  onConfirm: () => void;
  onClose: () => void;
}

/**
 * Sets or extends an organization's trial window.
 *
 * Both fields go through `TextInput`, which generates the id and wires `htmlFor`, `aria-invalid`
 * and `aria-describedby` itself. A bare `<input type="date">` with a nearby `<label>` is the most
 * common way a date field ends up unlabelled here.
 *
 * **A problem with the dates is attached to the field it is about, and focus moves there.**
 * Association alone is not enough: `TextInput` renders its error in a plain span with no
 * `role="alert"`, and `aria-describedby` is only read out once focus reaches the input. Pressing
 * Save leaves focus on the button, so without the move a screen reader user gets silence and no
 * idea why nothing happened. Moving focus to the offending control announces its label, its
 * invalid state and the reason together, and puts the caret where the fix has to be typed.
 *
 * **Every rejection moves focus, including a repeat of the one already showing.** Save pressed
 * twice on the same bad dates is two refusals, and `errorSeq` is what makes the second one a
 * distinct event: comparing the message and the field alone, the effect below would not run, focus
 * would stay on the Save button, and the dialog would read as frozen.
 *
 * A server refusal is nobody's field, so it stays a `role="alert"` of its own and does not steal
 * focus from the dialog.
 */
export const SetTrialModal = ({
  isOpen,
  organizationName,
  isEditing,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  loading,
  error,
  errorTarget,
  errorField,
  errorSeq,
  onConfirm,
  onClose,
}: SetTrialModalProps) => {
  const dateError = errorTarget === 'dates' ? error : null;
  const requestError = errorTarget === 'request' ? error : null;

  const startRef = useRef<HTMLInputElement>(null);
  const endRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!dateError || !errorField) return;
    (errorField === 'start' ? startRef : endRef).current?.focus();
    // errorSeq earns its place in the dependencies by being the only one of the three that changes
    // when the same dates are rejected a second time.
  }, [dateError, errorField, errorSeq]);

  return (
    <ConfirmModal
      isOpen={isOpen}
      title={isEditing ? 'Edit trial dates' : 'Start a trial'}
      message={`Set the trial window for "${organizationName}". Trials are for notices only: when one ends nothing is switched off, and pausing an organization stays a separate action.`}
      confirmText={loading ? 'Saving...' : 'Save dates'}
      busy={loading}
      onConfirm={onConfirm}
      onClose={onClose}
    >
      <div className="org-trial-form">
        <TextInput
          ref={startRef}
          label="Trial starts"
          type="date"
          value={startDate}
          onChange={(e) => onStartDateChange(e.target.value)}
          disabled={loading}
          error={errorField === 'start' ? dateError ?? undefined : undefined}
          data-testid="org-trial-start"
        />
        <TextInput
          ref={endRef}
          label="Trial ends"
          type="date"
          value={endDate}
          onChange={(e) => onEndDateChange(e.target.value)}
          disabled={loading}
          error={errorField === 'end' ? dateError ?? undefined : undefined}
          data-testid="org-trial-end"
        />
      </div>
      <p className="org-trial-form__hint">
        Dates are UTC, so every admin sets the same day whatever time zone they are in.
      </p>
      {requestError && (
        <div className="org-trial-form__error" role="alert" data-testid="org-trial-error">
          {requestError}
        </div>
      )}
    </ConfirmModal>
  );
};
